import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Modal } from '../ui';
import { authAPI } from '../../services/api';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EmailFormData {
  email: string;
}

interface ResetFormData {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors },
    reset: resetEmailForm
  } = useForm<EmailFormData>();

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
    reset: resetResetForm,
    watch
  } = useForm<ResetFormData>();

  const passwordValue = watch('newPassword');

  const title = useMemo(
    () => (step === 'request' ? 'Recuperar contrasena' : 'Verificar codigo y cambiar contrasena'),
    [step]
  );

  const closeAndReset = () => {
    setStep('request');
    setEmail('');
    resetEmailForm();
    resetResetForm();
    onClose();
  };

  const submitEmail = async (data: EmailFormData) => {
    setIsSubmitting(true);
    try {
      await authAPI.forgotPassword(data.email.trim().toLowerCase());
      setEmail(data.email.trim().toLowerCase());
      setStep('reset');
      toast.success('Si el correo existe, te enviamos un codigo.');
    } catch (error: any) {
      const message = error?.response?.data?.error || 'No se pudo procesar la solicitud.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitReset = async (data: ResetFormData) => {
    setIsSubmitting(true);
    try {
      await authAPI.verifyResetCode({ email, code: data.code.trim() });
      await authAPI.resetPasswordWithCode({
        email,
        code: data.code.trim(),
        newPassword: data.newPassword
      });
      toast.success('Contrasena actualizada. Ya puedes iniciar sesion.');
      closeAndReset();
    } catch (error: any) {
      const message = error?.response?.data?.error || 'No se pudo actualizar la contrasena.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeAndReset} title={title} size="md">
      {step === 'request' ? (
        <form className="space-y-4" onSubmit={handleSubmitEmail(submitEmail)} autoComplete="off">
          <p className="text-sm text-slate-600">
            Escribe tu correo y te enviaremos un codigo para restablecer tu contrasena.
          </p>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Correo electronico</label>
            <input
              type="email"
              autoComplete="email"
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="correo@empresa.com"
              disabled={isSubmitting}
              {...registerEmail('email', {
                required: 'El correo es obligatorio',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Correo invalido'
                }
              })}
            />
            {emailErrors.email && <p className="mt-1 text-xs text-red-600">{emailErrors.email.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-500"
          >
            {isSubmitting ? 'Enviando...' : 'Enviar codigo'}
          </button>
        </form>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmitReset(submitReset)} autoComplete="off">
          {/* El navegador asocia "primer texto + contraseñas" con login; este campo absorbe el autofill del correo */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="pointer-events-none absolute h-0 w-0 opacity-0"
          />

          <p className="text-sm text-slate-600">
            Ingresa el codigo enviado a <span className="font-semibold text-slate-800">{email}</span> y define una
            nueva contrasena.
          </p>

          <div>
            <label htmlFor="reset-verification-code" className="mb-1.5 block text-sm font-medium text-slate-700">
              Codigo (6 digitos)
            </label>
            <input
              id="reset-verification-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              data-1p-ignore
              data-lpignore="true"
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="123456"
              disabled={isSubmitting}
              {...registerReset('code', {
                required: 'El codigo es obligatorio',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'El codigo debe tener 6 digitos'
                },
                setValueAs: (value) => String(value).replace(/\D/g, '').slice(0, 6)
              })}
              autoComplete="one-time-code"
            />
            {resetErrors.code && <p className="mt-1 text-xs text-red-600">{resetErrors.code.message}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Nueva contrasena</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Minimo 6 caracteres"
              disabled={isSubmitting}
              {...registerReset('newPassword', {
                required: 'La contrasena es obligatoria',
                minLength: {
                  value: 6,
                  message: 'Debe tener al menos 6 caracteres'
                }
              })}
            />
            {resetErrors.newPassword && (
              <p className="mt-1 text-xs text-red-600">{resetErrors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Confirmar contrasena</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Repite la contrasena"
              disabled={isSubmitting}
              {...registerReset('confirmPassword', {
                required: 'Confirma la contrasena',
                validate: (value) => value === passwordValue || 'Las contrasenas no coinciden'
              })}
            />
            {resetErrors.confirmPassword && (
              <p className="mt-1 text-xs text-red-600">{resetErrors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('request')}
              disabled={isSubmitting}
              className="w-1/2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed"
            >
              Volver
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-1/2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-500"
            >
              {isSubmitting ? 'Guardando...' : 'Cambiar contrasena'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ForgotPasswordModal;
